import httpClient from '@/app/utils/axios';
import { Result } from '@/app/api/models/baseModel';
import { Classroom } from '@/app/api/models/classroom';
import { Module } from '@/app/api/models/module';
import { Session } from '@/app/api/models/session';
import { BaseModuleState } from '@/app/services/modules/BaseModuleService';
import { SessionModule } from './models/sessionModule';
import { axiosQueue } from '@/app/utils/axiosQueue';
import { isTeacher } from '@/app/helpers/userHelper';

interface ClassroomSession {
    session: Session;
    state: BaseModuleState;
    module: Module;
}

export function storeSessionModule(
    classroom: Classroom,
    module: Module,
    options: Record<string, unknown>,
    curriculumId?: number | null,
) {
    const params = {
        classroom_id: classroom.id,
        curriculum_id: curriculumId,
        module_id: module.id,
        options,
    };

    return httpClient.post<Result<ClassroomSession>>(route('app.teacher.session-modules.store'), params);
}

export function updateModuleResult<EventResponse = unknown>(
    sessionModule: SessionModule,
    event: string,
    data: unknown,
) {
    return httpClient.put<
        Result<{
            event_response: EventResponse;
            state: BaseModuleState;
        }>
    >(route('app.teacher.session-modules.update', { sessionModule }), {
        event,
        data,
    });
}

export function updateStudentModuleResult<EventResponse = unknown>(
    sessionModule: SessionModule,
    event: string,
    data: unknown,
    preserveWhitespace?: boolean,
) {
    return axiosQueue.enqueue(() =>
        httpClient.put<
            Result<{
                event_response: EventResponse;
                state: BaseModuleState;
            }>
        >(route('app.session-modules.update', { sessionModule }), {
            event,
            data,
            preserve_whitespace: preserveWhitespace,
        }),
    );
}

export function doneModule(sessionModule: SessionModule) {
    return httpClient.put<Result<ClassroomSession>>(route('app.teacher.session-modules.done', { sessionModule }));
}

export function getSessionModuleResult(sessionModule: SessionModule, isAllStudents?: boolean) {
    const routeName = isTeacher() ? 'app.teacher.session-modules.show' : 'app.session-modules.show';

    return httpClient.get<
        Result<{
            module: Module;
            session_module: SessionModule;
            students: {
                [studentId: number]: unknown;
            };
        }>
    >(route(routeName, { sessionModule }), {
        params: {
            all_students: isAllStudents,
        },
    });
}

export function getSessionModuleStudentsResult(sessionModule: SessionModule) {
    const routeName = isTeacher() ? 'app.teacher.session-modules.student-result' : 'app.session-modules.student-result';

    return httpClient.get<
        Result<{
            students: {
                [studentId: number]: unknown;
            };
            student_states: {
                [studentId: number]: unknown;
            };
        }>
    >(route(routeName, { sessionModule }));
}

export interface WritingReviewGuide {
    tip: string;
    example: string;
}

export interface WritingReview {
    can_submit: boolean;
    student_friendly_comment: string;
    strengths: string[];
    revision_guide: WritingReviewGuide[];
}

/** 학생 글 제출 전 AI 코칭 피드백 요청 - 학생용 */
export function requestWritingReview(
    sessionModule: SessionModule,
    params: { question_id: number; title?: string; text: string },
) {
    return httpClient.post<Result<WritingReview>>(
        route('app.session-modules.llm-task', { sessionModule, task: 'writing_review' }),
        params,
        // 4xx 발생 시 전역 새로고침을 막고, 실패는 호출부(AiWritingHelper)의 catch에서 처리한다.
        {
            errorCallback: () => {
                /* no-op: 호출부에서 처리 */
            },
        },
    );
}
